import SendIcon from "@mui/icons-material/Send";
import {
  Box,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Typography,
} from "@mui/material";
import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import chatIcon from "../../../assets/icons/chatIcon.svg";
import CustomInput from "../../../global/components/CustomInput/CustomInput";
import CustomSelect from "../../../global/components/CustomSelect/CustomSelect";
import { handleErrorMessage } from "../../../helpers/methods";
import ragChatbotStyles from "../RagChatbot.styles";
import {
  deleteSessionById,
  getAllSessions,
  getRagAsk,
  getSessionById,
} from "../RagChatbotService";
import ChatHistory, { ChatSession } from "./ChatHistory";
import {
  ChatMessagesSkeleton,
  TypingIndicatorSkeleton,
} from "./SkeletonLoaders";

interface Chat {
  type: string;
  content: string;
  metaData?: any[];
}

interface CustomProps {
  setIsLoading: Function;
  selectedFileInfo: { fileName: string; category: string } | null;
  setSelectedFileInfo: Function;
}

const ChatArea = ({
  setIsLoading,
  selectedFileInfo,
  setSelectedFileInfo,
}: CustomProps) => {
  const classes = ragChatbotStyles;
  const { t } = useTranslation();
  const chatEndRef = useRef<HTMLDivElement>(null);
  const [chatSessions, setChatSessions] = useState<ChatSession[]>([]);
  const [currentChatId, setCurrentChatId] = useState<string | null>(null);
  const [chat, setChat] = useState<Chat[]>([]);
  const [content, setContent] = useState<string>("");
  const [isLoadingChat, setIsLoadingChat] = useState<boolean>(false);
  const [isLoadingSessions, setIsLoadingSessions] = useState<boolean>(false);
  const [isWaitingResponse, setIsWaitingResponse] = useState<boolean>(false);
  const [selectedCategory, setSelectedCategory] = useState<string>("Other");
  console.log("selectedFileInfo", selectedFileInfo);

  const categories = [
    { value: "Other", content: "Other" },
    { value: "Marriage", content: "Marriage" },
    { value: "Death", content: "Death" },
    { value: "Birth", content: "Birth" },
  ];

  useEffect(() => {
    scrollToBottom();
  }, [chat]);

  useEffect(() => {
    loadChatSessions();
  }, []);

  const scrollToBottom = () => {
    const chatArea = document.querySelector(".scrollStyle");
    if (chatArea) {
      chatArea.scrollTop = chatArea.scrollHeight;
    }
  };

  const loadChatSessions = async () => {
    try {
      setIsLoadingSessions(true);
      const response = await getAllSessions();
      if (response && Array.isArray(response)) {
        const sessions = response.map((session: any) => ({
          id: session.id,
          name: session.name,
          args: session.args || {},
        }));
        setChatSessions(sessions);
        if (selectedFileInfo) {
          const chatSession = sessions.find(
            (s) => s.args.filename === selectedFileInfo.fileName
          );
          if (chatSession) {
            await loadChatById(chatSession.id);
          }
        }
      }
    } catch (error: any) {
      console.error("Error loading chat sessions:", error);
      handleErrorMessage(error);
    } finally {
      setIsLoadingSessions(false);
    }
  };

  const loadChatById = async (sessionId: string) => {
    try {
      setIsLoadingChat(true);
      const response = await getSessionById(sessionId);
      if (response && Array.isArray(response)) {
        setChat(response);
        setCurrentChatId(sessionId);
      }
    } catch (error: any) {
      console.error("Error loading chat by ID:", error);
      handleErrorMessage(error);
      setChat([]);
    } finally {
      setIsLoadingChat(false);
    }
  };

  const handleSubmit = async () => {
    if (!content.trim()) return;

    try {
      setIsWaitingResponse(true);
      const questionText: string = content;
      setContent("");
      const question: Chat = { type: "question", content: questionText };
      const args = selectedFileInfo
        ? {
            filename: selectedFileInfo.fileName,
            category: selectedFileInfo.category,
          }
        : { category: selectedCategory };
      setChat((prevChat) => [...prevChat, question]);
      const response = await getRagAsk(
        questionText,
        args,
        currentChatId || undefined
      );
      setChat((prevChat) => [...prevChat, response]);
      if (!currentChatId && response.sessionId) {
        setCurrentChatId(response.sessionId);
        await loadChatSessions();
      }
      setContent("");
    } catch (error: any) {
      setChat((prevChat) => prevChat.slice(0, -1));
      handleErrorMessage(error);
    } finally {
      setIsWaitingResponse(false);
    }
  };

  const handleKeypress = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleInput = (event: ChangeEvent<HTMLInputElement>) => {
    setContent(event.target.value);
  };

  const handleSelectChat = async (session: ChatSession) => {
    if (session.id !== currentChatId) {
      if (session.args.filename) {
        setSelectedFileInfo({
          fileName: session.args.filename,
          category: session.args.category,
        });
      } else {
        setSelectedFileInfo(null);
      }
      await loadChatById(session.id);
    }
  };

  const handleNewChat = () => {
    setCurrentChatId(null);
    setChat([]);
    setSelectedFileInfo(null);
  };

  const handleDeleteChat = async (chatId: string) => {
    try {
      setIsLoading(true);
      await deleteSessionById(chatId);
      setChatSessions((prevSessions) =>
        prevSessions.filter((session) => session.id !== chatId)
      );
      if (currentChatId === chatId) {
        setCurrentChatId(null);
        setChat([]);
      }
    } catch (error: any) {
      console.error("Error deleting chat session:", error);
      handleErrorMessage(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCategoryChange = (event: any) => {
    setSelectedCategory(event.target.value);
  };

  const chatAreaHeader = () => {
    return (
      <Box
        sx={{
          borderBottom: "1px solid rgba(255, 255, 255, 0.14)",
          p: 1,
          pt: 0,
        }}
      >
        {!selectedFileInfo && (
          <CustomSelect
            menuItems={categories}
            onChange={handleCategoryChange}
            id={"Category"}
            name={"Category"}
            value={selectedCategory}
            customClasses={classes.selectField}
          />
        )}
        {selectedFileInfo && (
          <Box display="flex" flexDirection={"column"}>
            <Box sx={classes.labelTypo} display="inline-block">
              {t("selectedFile")}:
              <Typography
                display="inline-block"
                sx={classes.valueTypo}
                pl={0.5}
              >
                {selectedFileInfo.fileName}
              </Typography>
            </Box>
            <Box sx={classes.labelTypo} display="inline-block">
              {t("category")}:
              <Typography
                display="inline-block"
                sx={classes.valueTypo}
                pl={0.5}
              >
                {selectedFileInfo.category}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  const getWelcomeScreen = () => {
    return (
      <>
        <Box sx={classes.welcomeBoxWrapper}>
          <Box sx={classes.welcomeBox}>
            {chatAreaHeader()}
            <Box
              display={"flex"}
              flexDirection={"column"}
              alignItems={"center"}
              justifyContent={"center"}
              height={"100%"}
            >
              <Box
                component="img"
                src={chatIcon}
                alt="Welcome"
                sx={classes.chatImage}
              />
              <Typography sx={classes.welcomeHeader}>
                {t("ragChatbotHeader")}
              </Typography>
            </Box>
          </Box>
        </Box>
      </>
    );
  };

  const getChat = () => {
    const hasMessages = chat.length > 0;

    if (isLoadingChat) {
      return <ChatMessagesSkeleton />;
    }

    if (!hasMessages) {
      return getWelcomeScreen();
    }

    return (
      <>
        <Box className="scrollStyle" sx={classes.scrollStyle}>
          {chatAreaHeader()}
          {chat.map((message: Chat, idx) => {
            const isQuestion = message.type === "question";
            return (
              <Box
                key={idx}
                sx={[
                  classes.messageBox,
                  isQuestion ? classes.messageBoxRight : classes.messageBoxLeft,
                ]}
                pt={1}
              >
                <Box
                  sx={[
                    classes.message,
                    isQuestion ? classes.messageRight : classes.messageLeft,
                  ]}
                >
                  <Typography sx={classes.messageText}>
                    <ReactMarkdown
                      remarkPlugins={[require("remark-gfm")]}
                      components={{
                        p: (props) => <p style={{ margin: 0 }} {...props} />,
                        ul: (props) => (
                          <ul
                            style={{
                              margin: 0,
                              paddingLeft: 20,
                              display: "grid",
                            }}
                            {...props}
                          />
                        ),
                        ol: (props) => (
                          <ol
                            style={{
                              margin: 0,
                              paddingLeft: 30,
                              display: "grid",
                            }}
                            {...props}
                          />
                        ),
                        h1: (props) => <h1 style={{ margin: 0 }} {...props} />,
                        h2: (props) => <h2 style={{ margin: 0 }} {...props} />,
                        h3: (props) => <h3 style={{ margin: 0 }} {...props} />,
                        h4: (props) => <h4 style={{ margin: 0 }} {...props} />,
                        h5: (props) => <h5 style={{ margin: 0 }} {...props} />,
                        h6: (props) => <h6 style={{ margin: 0 }} {...props} />,
                        a: (props) => (
                          <a
                            style={{
                              margin: 0,
                              color: "#8A31FF",
                              textDecoration: "underline",
                            }}
                            target="_blank"
                            rel="noopener noreferrer"
                            {...props}
                          />
                        ),
                      }}
                    >
                      {(message.content || "").replace(/\\n/g, "\n")}
                    </ReactMarkdown>
                  </Typography>
                  {!isQuestion &&
                    Array.isArray(message.metaData) &&
                    message.metaData &&
                    message.metaData.length > 0 && (
                      <Box sx={classes.metaDataBox}>
                        {message.metaData.map((meta, i) => (
                          <Box
                            key={i}
                            sx={classes.metaDataItem}
                            component="a"
                            href={meta.url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Typography sx={classes.metaDataText}>
                              {meta?.filename || ""}
                            </Typography>
                            <Typography sx={classes.metaDataPage}>
                              {meta?.page_number !== undefined
                                ? `Page: ${meta.page_number}`
                                : ""}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                </Box>
              </Box>
            );
          })}

          {isWaitingResponse && <TypingIndicatorSkeleton />}
          <Box ref={chatEndRef} />
        </Box>
      </>
    );
  };

  const getFooter = () => {
    return (
      <Box sx={classes.footer}>
        <CustomInput
          multiline
          rows={1}
          value={content}
          onChange={handleInput}
          onKeyPress={handleKeypress}
          placeHolder={"Write your message..."}
          customClasses={classes.textField}
          disabled={isWaitingResponse} // Disable input while waiting for response
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={handleSubmit}
                  disabled={!content.trim() || isWaitingResponse} // Disable button while waiting
                  sx={{
                    background:
                      !content.trim() || isWaitingResponse
                        ? "#8A31FF50"
                        : "#8A31FF",
                    "&:hover": {
                      background:
                        !content.trim() || isWaitingResponse
                          ? "#8A31FF50"
                          : "#8a58cb",
                    },
                  }}
                >
                  <SendIcon htmlColor="#ffffff" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>
    );
  };

  const getChatArea = () => {
    return (
      <Grid container sx={{ placeContent: "center" }}>
        <Grid
          item
          xl={2.9}
          lg={3.9}
          md={3.9}
          sm={12}
          xs={12}
          pr={{ md: 1, xs: 0 }}
          pb={{ md: 0, xs: 1 }}
        >
          <Box p={2} pl={0} pr={{ md: 1, xs: 0 }} height="calc(100vh - 285px)">
            <ChatHistory
              chatSessions={chatSessions}
              currentChatId={currentChatId}
              onSelectChat={handleSelectChat}
              onNewChat={handleNewChat}
              onDeleteChat={handleDeleteChat}
              isLoading={isLoadingSessions}
            />
          </Box>
        </Grid>
        <Divider
          orientation="vertical"
          flexItem
          sx={{
            border: "1px solid rgba(255, 255, 255, 0.2)",
          }}
        />
        <Grid item xl={9} lg={8} md={8} sm={12} xs={12}>
          <Box pt={{ md: 2, xs: 0 }} pr={0} pb={1} pl={{ md: 2, xs: 0 }}>
            {getChat()}
            {getFooter()}
          </Box>
        </Grid>
      </Grid>
    );
  };

  return getChatArea();
};

export default ChatArea;
